import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * Collapsible JSON viewer — used for decoded session tokens, freebet configs and
 * the raw-bundle escape hatch. Deliberately unopinionated: it must render
 * whatever shape a game happens to store without assuming a schema.
 */
export function JsonTree({
  value,
  name,
  depth = 0,
  defaultOpen = 2,
}: {
  value: unknown;
  name?: string;
  depth?: number;
  defaultOpen?: number;
}) {
  const [open, setOpen] = useState(depth < defaultOpen);

  if (value === null) return <Leaf name={name} text="null" cls="text-text-faint" />;
  if (value === undefined) return <Leaf name={name} text="undefined" cls="text-text-faint" />;

  const t = typeof value;
  if (t === 'string') return <Leaf name={name} text={`"${value as string}"`} cls="text-ok" />;
  if (t === 'number') return <Leaf name={name} text={String(value)} cls="text-info tnum" />;
  if (t === 'boolean')
    return <Leaf name={name} text={String(value)} cls="text-accent-warm" />;

  const isArray = Array.isArray(value);
  const entries = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>);

  if (entries.length === 0) {
    return <Leaf name={name} text={isArray ? '[]' : '{}'} cls="text-text-faint" />;
  }

  return (
    <div className={cn(depth > 0 && 'ml-3 border-l border-hairline/60 pl-2')}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="group flex items-center gap-1 py-[1px] text-left hover:bg-surface-raised/60"
      >
        <ChevronRight
          size={10}
          className={cn('shrink-0 text-text-faint transition-transform', open && 'rotate-90')}
        />
        {name !== undefined && <span className="font-mono text-[11.5px] text-text-dim">{name}</span>}
        <span className="font-mono text-[11px] text-text-faint">
          {isArray ? `[${entries.length}]` : `{${entries.length}}`}
        </span>
      </button>

      {open && (
        <div>
          {entries.map(([k, v]) => (
            <JsonTree key={k} name={k} value={v} depth={depth + 1} defaultOpen={defaultOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

function Leaf({ name, text, cls }: { name?: string; text: string; cls: string }) {
  return (
    <div className="flex items-baseline gap-1.5 py-[1px] pl-[14px]">
      {name !== undefined && (
        <span className="shrink-0 font-mono text-[11.5px] text-text-dim">{name}:</span>
      )}
      <span className={cn('break-all font-mono text-[11.5px]', cls)}>{text}</span>
    </div>
  );
}
